import { format } from "date-fns";
import { dateTimeFormat } from "../../common/helpers/formatDateTime";
import {
  UserMessageChannel,
  UserMessageContext,
  UserMessageRow,
} from "./userMessagesTypes";

const USER_MESSAGE_CHANNELS: UserMessageChannel[] = ["Email", "InApp"];

export const toUserMessageChannels = (
  sendAsEmail: boolean,
  sendAsInApp: boolean,
): UserMessageChannel[] => {
  const channels: UserMessageChannel[] = [];
  if (sendAsEmail) channels.push("Email");
  if (sendAsInApp) channels.push("InApp");
  return channels;
};

export const normalizeUserMessageChannels = (
  channels: readonly string[] | null | undefined,
): UserMessageChannel[] => {
  const present = new Set(channels ?? []);
  return USER_MESSAGE_CHANNELS.filter((channel) => present.has(channel));
};

export const isInAppNotice = (message: {
  channels?: readonly string[] | null;
}): boolean => {
  const channels = normalizeUserMessageChannels(message.channels);
  return channels.includes("InApp") && !channels.includes("Email");
};

export const getInAppReadReceipt = (message: {
  channels?: readonly string[] | null;
  readAt?: number | null;
  processedAt?: number | null;
}): { kind: "read"; date: string } | { kind: "unread" } | null => {
  if (!normalizeUserMessageChannels(message.channels).includes("InApp"))
    return null;
  if (!message.processedAt) return null;
  if (message.readAt) {
    return {
      kind: "read",
      date: format(new Date(message.readAt), dateTimeFormat),
    };
  }
  return { kind: "unread" };
};

export const getUserMessageChannelLabelKey = (
  channels: readonly string[] | null | undefined,
): "channelEmail" | "channelInApp" | "channelEmailAndInApp" => {
  const normalized = normalizeUserMessageChannels(channels);
  const hasEmail = normalized.includes("Email");
  const hasInApp = normalized.includes("InApp");
  if (hasEmail && hasInApp) return "channelEmailAndInApp";
  if (hasInApp) return "channelInApp";
  return "channelEmail";
};

export const chunkArray = <T>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

export const getCustomerOrgIdFromContext = (
  context?: Pick<UserMessageContext, "type" | "customerOrgId"> | null,
): string | undefined => {
  if (context?.type !== "TalentCommonMessage") return undefined;
  return context.customerOrgId || undefined;
};

export const uniqueIds = (
  ids: Array<string | null | undefined>,
): string[] => [...new Set(ids.filter((id): id is string => Boolean(id)))];

export const getSenderUserIds = (messages: UserMessageRow[]): string[] =>
  uniqueIds(messages.map((msg) => msg.createdByUserId));

export const getCustomerOrgIdsFromMessages = (
  messages: UserMessageRow[],
): string[] =>
  uniqueIds(messages.map((msg) => getCustomerOrgIdFromContext(msg.context)));

export const joinMessageMeta = (
  ...parts: Array<string | null | undefined>
): string => parts.filter((part): part is string => Boolean(part)).join(" · ");

export const getSentStatusDisplay = (
  message: Pick<UserMessageRow, "processedAt" | "status">,
):
  | { kind: "sent"; date: string }
  | { kind: "processing" }
  | { kind: "failed" } => {
  if (message.processedAt) {
    return {
      kind: "sent",
      date: format(new Date(message.processedAt), dateTimeFormat),
    };
  }
  if (message.status === "Failed") return { kind: "failed" };
  return { kind: "processing" };
};

export const getMessageListSecondary = (
  message: Pick<UserMessageRow, "processedAt" | "status">,
  senderName: string | undefined,
  labels: { processing: string; sendFailed: string },
): { text: string; isError: boolean } => {
  const detail = message.processedAt
    ? format(new Date(message.processedAt), dateTimeFormat)
    : message.status === "Processing"
      ? labels.processing
      : message.status === "Failed"
        ? labels.sendFailed
        : undefined;

  return {
    text: joinMessageMeta(senderName, detail),
    isError: !message.processedAt && message.status === "Failed",
  };
};

export const getSenderDisplayName = (
  senderName: string | undefined,
  isLookupSettled: boolean,
  fallback: string,
): string | undefined => {
  if (senderName) return senderName;
  if (isLookupSettled) return fallback;
  return undefined;
};

export const getMissingUserIds = (
  neededIds: string[],
  entities: Record<string, unknown>,
  alreadyRequestedIds: Iterable<string> = [],
): string[] => {
  const requested = new Set(alreadyRequestedIds);
  return neededIds.filter((id) => !entities[id] && !requested.has(id));
};
